/**
 * FieldOverrideStorage: adapter that persists FieldOverride values in localStorage.
 * All I/O lives here; domain/field-override.ts stays pure.
 */

import type { FieldId } from "../../domain/types.js";
import type { FieldOverride } from "../../domain/field-override.js";
import { serializeOverride, parseOverride } from "../../domain/field-override.js";

const LEGACY_KEY = (id: FieldId): string => `gc_field_override_${id}`;
const CAMPAIGN_KEY = (campaignId: string, id: FieldId): string =>
  `gc_field_override_${campaignId}_${id}`;

export interface FieldOverrideStorage {
  getOverride(fieldId: FieldId): FieldOverride | null;
  setOverride(fieldId: FieldId, override: FieldOverride): void;
  clearOverride(fieldId: FieldId): void;
}

export function createFieldOverrideStorage(campaignId?: string): FieldOverrideStorage {
  const key = (fieldId: FieldId): string =>
    campaignId ? CAMPAIGN_KEY(campaignId, fieldId) : LEGACY_KEY(fieldId);
  const fallbackKey = (fieldId: FieldId): string | null =>
    campaignId === "sanctified" ? LEGACY_KEY(fieldId) : null;

  return {
    getOverride(fieldId: FieldId): FieldOverride | null {
      if (typeof localStorage === "undefined") return null;
      const raw = localStorage.getItem(key(fieldId)) ?? (
        fallbackKey(fieldId) ? localStorage.getItem(fallbackKey(fieldId)!) : null
      );
      if (!raw) return null;
      return parseOverride(raw);
    },

    setOverride(fieldId: FieldId, override: FieldOverride): void {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(key(fieldId), serializeOverride(override));
    },

    clearOverride(fieldId: FieldId): void {
      if (typeof localStorage === "undefined") return;
      localStorage.removeItem(key(fieldId));
      const fallback = fallbackKey(fieldId);
      if (fallback) localStorage.removeItem(fallback);
    },
  };
}
